import {isFeatureEnabled} from '@hconnect/apiclient'
import {GenericInput, GenericSelect} from '@hm/fundamental'
import React, {useEffect, useState} from 'react'
import {Controller, FieldValues} from 'react-hook-form'

import {api} from '../../../App.store'
import {Option} from '../../../common/AutoSelectDropdown'
import {useFeaturesCheckFetcher} from '../../../common/hooks/useFeaturesCheckFetcher'
import {useTranslation} from '../../../hooks/useTranslation'
import {SelectGrouped} from '../../../pages/ManageUser/components/SelectGrouped'
import {UserInformationInputSkeleton} from '../Skeleton/UserInformationInputSkeleton'

import {UserInformationProps} from './types'

type UserMarketInputProps<T extends FieldValues> = UserInformationProps<T> & {
  marketSelectValues: Option[]
  marketName?: string | null
}

export const UserMarketInput = <T extends FieldValues>({
  readonlyMode,
  formMethods,
  marketName,
  marketSelectValues,
  isLoading,
  name
}: UserMarketInputProps<T>) => {
  const {t} = useTranslation()
  const values = formMethods.watch()
  const [shouldMarkAsMandatory, setShouldMarkAsMandatory] = useState(false)
  const {data: features} = useFeaturesCheckFetcher(api)

  const handleCheckFeatures = () => {
    const shouldMarketBeMandatory = isFeatureEnabled(
      features || [],
      {country: values.country},
      'MarketMandatory'
    )
    setShouldMarkAsMandatory(shouldMarketBeMandatory)
  }

  useEffect(() => {
    void handleCheckFeatures()
  }, [values.country, readonlyMode])

  if (isLoading) {
    return <UserInformationInputSkeleton />
  }

  return (
    <>
      {readonlyMode ? (
        <GenericInput
          label={t('userInformationForm.marketId')}
          value={marketName || '-'}
          isReadOnly={true}
        />
      ) : (
        <Controller
          name={name}
          control={formMethods.control}
          rules={{
            required: shouldMarkAsMandatory ? t('userInformationForm.marketIdIsRequired') : false
          }}
          render={({field, fieldState}) => (
            <SelectGrouped
              {...field}
              onChange={(value) => field.onChange(value)}
              data-test-id={`userInformationForm-${field.name}`}
              showSearch={true}
              emptyLabel={t('userInformationForm.selectMarket')}
              searchPlaceholder={t('userInformationForm.searchMarket')}
              inputLabel={t('userInformationForm.marketId')}
              value={(field.value as string) || '-'}
              isReadOnly={readonlyMode}
              error={
                !values.country
                  ? t('userInformationForm.marketIdHelperText')
                  : fieldState.error
                    ? fieldState.error.message
                    : ''
              }
              options={marketSelectValues}
            />
          )}
        />
      )}
    </>
  )
}
